import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button/index.js";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { cn } from "../../lib/utils.js";

export function ReportPaginationBar({
  page,
  pageSize,
  totalCount,
  totalPages,
  pageSizes,
  onPageChange,
  onPageSizeChange,
  showingLabel,
  pageOfLabel,
  prevLabel,
  nextLabel,
  pageSizeLabel,
  testIdPrefix,
  rangeStart,
  rangeEnd,
  className,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  pageSizes: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  showingLabel: string;
  pageOfLabel: string;
  prevLabel: string;
  nextLabel: string;
  pageSizeLabel: string;
  testIdPrefix: string;
  rangeStart: number;
  rangeEnd: number;
  className?: string;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const format = (n: number) => new Intl.NumberFormat(i18n.language).format(n);

  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border/60 bg-surface px-3 py-2",
        className,
      )}
      data-testid={`${testIdPrefix}-pagination`}
    >
      <p className="m-0 text-[0.75rem] text-muted tabular-nums">
        {showingLabel
          .replace("{{start}}", format(rangeStart))
          .replace("{{end}}", format(rangeEnd))
          .replace("{{total}}", format(totalCount))}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-[0.75rem] text-muted">
          <span className="sr-only md:not-sr-only">{pageSizeLabel}</span>
          <SimpleSelect
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
            ariaLabel={pageSizeLabel}
            triggerClassName="h-8 w-[4.25rem] min-w-0 text-start text-[0.75rem]"
            options={pageSizes.map((size) => ({
              value: String(size),
              label: String(size),
            }))}
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          data-testid={`${testIdPrefix}-prev`}
        >
          {prevLabel}
        </Button>
        <span className="text-[0.75rem] tabular-nums text-muted" data-testid={`${testIdPrefix}-page-of`}>
          {pageOfLabel
            .replace("{{page}}", format(page))
            .replace("{{totalPages}}", format(Math.max(totalPages, 1)))}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          data-testid={`${testIdPrefix}-next`}
        >
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
